/**
 * useSessionTimer — derives the remaining session lifetime from a
 * caller-supplied `getExpiry` function (Unix epoch seconds), refreshed
 * every 30s. Used by the SessionIndicator chip in the app shell to
 * surface "session expires in 12m" to users.
 *
 * No backend call — the MSAL-aware caller passes a `getExpiry` that
 * reads the active account's id-token `exp` claim directly. When MSAL
 * silent-refreshes the token (transparent on most flows), the next tick
 * picks up the new exp and the chip resets.
 *
 * Returns null secondsRemaining when there's no active session
 * (logged out, /login route, local-dev X-Dev-User without an exp).
 */
import { useEffect, useState } from 'react';
const TICK_MS = 30_000;
export function useSessionTimer({ getExpiry, warnThresholdSeconds = 600, criticalThresholdSeconds = 300, }) {
    const [secondsRemaining, setSecondsRemaining] = useState(() => {
        const exp = getExpiry();
        return exp === null ? null : Math.floor(exp - Date.now() / 1000);
    });
    useEffect(() => {
        const tick = () => {
            const exp = getExpiry();
            if (exp === null) {
                setSecondsRemaining(null);
                return;
            }
            setSecondsRemaining(Math.floor(exp - Date.now() / 1000));
        };
        tick();
        const id = setInterval(tick, TICK_MS);
        return () => clearInterval(id);
    }, [getExpiry]);
    const isExpiringSoon = secondsRemaining !== null && secondsRemaining > 0 && secondsRemaining < warnThresholdSeconds;
    const isCritical = secondsRemaining !== null && secondsRemaining > 0 && secondsRemaining < criticalThresholdSeconds;
    return { secondsRemaining, isExpiringSoon, isCritical };
}
